import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import axios from 'axios';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Available themes
export const AVAILABLE_THEMES = {
  Default: { primary: '#d4af37', text: '#f3f4f6', background: '#0d0d15' },
  Dark: { primary: '#d4af37', text: '#f3f4f6', background: '#0d0d15' },
  Gold: { primary: '#d4af37', text: '#f3f4f6', background: '#121118' },
  Blue: { primary: '#3b82f6', text: '#f3f4f6', background: '#0f172a' }
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userTheme, setUserTheme] = useState('Dark');
  const [error, setError] = useState(null);

  // Helper to fetch valid Bearer ID Token for API calls
  const getIdToken = useCallback(async (forceRefresh = false) => {
    if (!auth.currentUser) return null;
    try {
      return await auth.currentUser.getIdToken(forceRefresh);
    } catch (err) {
      console.error('Error fetching ID token:', err);
      return null;
    }
  }, []);

  // Sync user with backend & determine approval / admin status
  const syncUserWithBackend = async (user) => {
    try {
      const token = await user.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.post(`${apiUrl}/api/users/sync`, {
        displayName: user.displayName,
        photoURL: user.photoURL,
        profession: user.displayName?.includes('Dr') ? 'Medicine' : 'Juror'
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 6000
      });
      return res.data;
    } catch (err) {
      console.warn('Backend sync warning (falling back to Firestore directly):', err.message);
      return null;
    }
  };

  // Fetch user data from Firestore
  const fetchUserData = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({ 
          ...data,
          id: uid,
          uid: uid,
          isApproved: Boolean(data.isApproved || data.isAdmin),
          isAdmin: Boolean(data.isAdmin),
          points: data.points || 0,
          contemptBadges: data.contemptBadges || []
        });
        return data;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError(error.message);
      return null;
    }
  };

  // Update user theme
  const updateUserTheme = async (themeName) => {
    if (!currentUser) return;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { theme: themeName });
      setUserTheme(themeName);
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        setCurrentUser(user);
        // Sync with backend to handle first-user admin bootstrap & approval tracking
        await syncUserWithBackend(user);
        await fetchUserData(user.uid);
      } else {
        setCurrentUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    isApproved: Boolean(userData?.isApproved || userData?.isAdmin),
    isAdmin: Boolean(userData?.isAdmin),
    userTheme,
    updateUserTheme,
    fetchUserData,
    getIdToken,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;